/**
 * InfrastructureDashboard — Main page for managing infrastructure hosts and VMs
 */

import React, { useEffect, useState } from 'react';
import { useInfraStore } from '@/stores/infrastructure/infra-store';
import { formatMemory, vmStateBadge } from '@/services/api/infrastructureApi';
import { HostRegistrationForm } from '@/components/infrastructure/HostRegistrationForm';
import { VMCreateDialog } from '@/components/infrastructure/VMCreateDialog';
import { VMActionButtons } from '@/components/infrastructure/VMActionButtons';
import { ResourceUtilizationChart } from '@/components/infrastructure/ResourceUtilizationChart';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Server,
  Plus,
  RefreshCw,
  Monitor,
  Cpu,
  HardDrive,
  MemoryStick,
  Trash2,
  Settings,
  X,
  ArrowRightLeft,
} from 'lucide-react';

export const InfrastructureDashboard: React.FC = () => {
  const {
    hosts,
    selectedHostId,
    vms,
    resources,
    isLoading,
    isLoadingVMs,
    error,
    fetchHosts,
    selectHost,
    updateHost,
    deleteHost,
    transferHost,
    discoverResources,
    clearError,
  } = useInfraStore();

  const [showRegister, setShowRegister] = useState(false);
  const [showCreateVM, setShowCreateVM] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferOrgSlug, setTransferOrgSlug] = useState('');
  const [settingsForm, setSettingsForm] = useState({
    displayName: '',
    mode: 'safe' as string,
    criticality: 'standard' as string,
    description: '',
  });

  useEffect(() => {
    fetchHosts();
  }, [fetchHosts]);

  const selectedHost = hosts.find((h) => h.id === selectedHostId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Infrastructure</h1>
          <p className="text-muted-foreground">Manage hypervisor hosts and virtual machines</p>
        </div>
        <Button onClick={() => setShowRegister(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Register Host
        </Button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-destructive/15 text-destructive px-4 py-2 rounded flex items-center justify-between">
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={clearError}>Dismiss</Button>
        </div>
      )}

      {/* Host Cards */}
      {isLoading && hosts.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}><CardContent className="p-6"><Skeleton className="h-24 w-full" /></CardContent></Card>
          ))}
        </div>
      ) : hosts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Server className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No hosts registered</h3>
            <p className="text-muted-foreground mb-4">
              Register a Xen/XCP-ng host to start managing virtual machines.
            </p>
            <Button onClick={() => setShowRegister(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Register Your First Host
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {hosts.map((host) => {
            const isSelected = selectedHostId === host.id;
            const res = host.discoveredResources;
            return (
              <Card
                key={host.id}
                className={`cursor-pointer transition-colors ${
                  isSelected ? 'ring-2 ring-primary' : 'hover:bg-muted/50'
                }`}
                onClick={() => selectHost(isSelected ? null : host.id)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{host.displayName}</CardTitle>
                    <Badge variant="secondary">{host.provider.toUpperCase()}</Badge>
                  </div>
                  <CardDescription>{host.name}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {res?.cpuTotal && (
                      <div className="flex items-center gap-2">
                        <Cpu className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{res.cpuTotal} CPUs</span>
                      </div>
                    )}
                    {res?.memoryTotalMB && (
                      <div className="flex items-center gap-2">
                        <MemoryStick className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{formatMemory(res.memoryTotalMB)}</span>
                      </div>
                    )}
                    {res?.vmCount !== undefined && (
                      <div className="flex items-center gap-2">
                        <Monitor className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{res.vmCount} VMs</span>
                      </div>
                    )}
                    {res?.storageRepositories && (
                      <div className="flex items-center gap-2">
                        <HardDrive className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{res.storageRepositories.length} SRs</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Badge variant="outline" className="text-xs">
                      {host.mode}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {host.criticality}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Selected Host Detail */}
      {selectedHostId && selectedHost && (
        <div className="space-y-4">
          {/* Resource Utilization */}
          {resources && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Resource Utilization — {selectedHost.displayName}</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => discoverResources(selectedHostId)}
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Refresh
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSettingsForm({
                          displayName: selectedHost.displayName || '',
                          mode: selectedHost.mode || 'safe',
                          criticality: selectedHost.criticality || 'standard',
                          description: selectedHost.description || '',
                        });
                        setShowSettings(!showSettings);
                      }}
                    >
                      <Settings className="h-4 w-4 mr-1" />
                      Settings
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowTransfer(!showTransfer);
                        setTransferOrgSlug('');
                      }}
                    >
                      <ArrowRightLeft className="h-4 w-4 mr-1" />
                      Transfer
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => deleteHost(selectedHostId)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Remove Host
                    </Button>
                  </div>
                </div>
                {resources.cpuModel && (
                  <CardDescription>{resources.cpuModel}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <ResourceUtilizationChart resources={resources} />
              </CardContent>
            </Card>
          )}

          {/* Host Settings */}
          {showSettings && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Host Settings — {selectedHost.displayName}</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setShowSettings(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Display Name</label>
                    <input
                      type="text"
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={settingsForm.displayName}
                      onChange={(e) => setSettingsForm((f) => ({ ...f, displayName: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Mode</label>
                    <select
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={settingsForm.mode}
                      onChange={(e) => setSettingsForm((f) => ({ ...f, mode: e.target.value }))}
                    >
                      <option value="safe">Safe — requires confirmation for all destructive ops</option>
                      <option value="supervised">Supervised — agents can act but user is notified</option>
                      <option value="autonomous">Autonomous — agents can act independently</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Criticality</label>
                    <select
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={settingsForm.criticality}
                      onChange={(e) => setSettingsForm((f) => ({ ...f, criticality: e.target.value }))}
                    >
                      <option value="development">Development — non-critical, can be rebuilt</option>
                      <option value="standard">Standard — normal production workload</option>
                      <option value="critical">Critical — high-value, extra safeguards</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Description</label>
                    <input
                      type="text"
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      placeholder="Optional description for agent context"
                      value={settingsForm.description}
                      onChange={(e) => setSettingsForm((f) => ({ ...f, description: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-4">
                  <Button
                    size="sm"
                    onClick={async () => {
                      const ok = await updateHost(selectedHostId, settingsForm);
                      if (ok) {
                        setShowSettings(false);
                        fetchHosts();
                      }
                    }}
                  >
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Transfer Host */}
          {showTransfer && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Transfer Host — {selectedHost.displayName}</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setShowTransfer(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <CardDescription>
                  Move this host to another organization. You must be a member of the target organization.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label className="text-sm font-medium mb-1 block">Target Organization Slug</label>
                    <input
                      type="text"
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      placeholder="e.g. terramatrix"
                      value={transferOrgSlug}
                      onChange={(e) => setTransferOrgSlug(e.target.value)}
                    />
                  </div>
                  <Button
                    size="sm"
                    disabled={!transferOrgSlug.trim()}
                    onClick={async () => {
                      const ok = await transferHost(selectedHostId, transferOrgSlug.trim());
                      if (ok) {
                        setShowTransfer(false);
                        setTransferOrgSlug('');
                      }
                    }}
                  >
                    <ArrowRightLeft className="h-4 w-4 mr-1" />
                    Transfer Host
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* VM Inventory */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Virtual Machines</CardTitle>
                <Button size="sm" onClick={() => setShowCreateVM(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Create VM
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingVMs ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : vms.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No VMs found on this host.
                </p>
              ) : (
                <div className="divide-y">
                  {vms.map((vm) => (
                    <div key={vm.id} className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-3">
                        <Monitor className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{vm.name}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{vm.cpuCount} CPU</span>
                            <span>{formatMemory(vm.memoryMB)}</span>
                            {vm.ipAddresses.length > 0 && (
                              <span>{vm.ipAddresses.join(', ')}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-xs ${vmStateBadge(vm.state)}`}>
                          {vm.state}
                        </Badge>
                        <VMActionButtons hostId={selectedHostId} vm={vm} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Dialogs */}
      <HostRegistrationForm open={showRegister} onClose={() => setShowRegister(false)} />
      {selectedHostId && (
        <VMCreateDialog
          open={showCreateVM}
          hostId={selectedHostId}
          onClose={() => setShowCreateVM(false)}
        />
      )}
    </div>
  );
};
